import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { affiliateService } from '../../services/affiliateService';

interface PlanOption {
  id: string;
  name: string;
}

interface TiersManagementProps {
  programId?: string;
}

const TiersManagement: React.FC<TiersManagementProps> = ({ programId }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [tiers, setTiers] = useState<Array<{ min_amount: number; rate: number }>>([{ min_amount: 0, rate: 10 }]);

  const fetchTiers = async () => {
    if (!programId) {
      setPlans([]);
      setSelectedPlanId('');
      setError('No program ID provided');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await affiliateService.getPlans(programId);
      const planList = Array.isArray(data) ? data : [];
      setPlans(planList.map((p: any) => ({ id: p.id, name: p.name })));
      if (planList.length && !selectedPlanId) {
        setSelectedPlanId(planList[0].id);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load plans');
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  const addTierRow = () => setTiers((t) => [...t, { min_amount: 0, rate: 0 }]);
  const removeTierRow = (idx: number) => setTiers((t) => t.filter((_, i) => i !== idx));
  const updateTierRow = (idx: number, key: 'min_amount' | 'rate', value: number) =>
    setTiers((t) => t.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!selectedPlanId) {
        toast.error('Select a plan to edit tiers');
        return;
      }
      // basic validation
      if (!tiers.length) {
        toast.error('Add at least one tier');
        return;
      }
      const normalized = tiers
        .map((t) => ({ min_amount: Number(t.min_amount), rate: Number(t.rate) }))
        .sort((a, b) => a.min_amount - b.min_amount);
      await affiliateService.upsertPlanTiers(selectedPlanId, normalized);
      toast.success('Tiers saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save tiers');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Partner Tiers</h2>
        <p className="text-sm text-gray-500">Define revenue-based tiers for a selected commission plan.</p>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Select Plan</label>
          <select
            className="border rounded px-3 py-2 w-full"
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
          >
            {plans.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2 flex items-end">
          <button onClick={addTierRow} className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-3 rounded">Add Tier</button>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading tiers...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <form onSubmit={handleSave} className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200 text-left text-sm text-gray-600">
                <th className="py-3 px-3">Min Amount</th>
                <th className="py-3 px-3">Rate</th>
                <th className="py-3 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t, idx) => (
                <tr key={idx} className="border-b border-gray-100 text-sm">
                  <td className="py-3 px-3">
                    <input type="number" className="border rounded px-3 py-2 w-full" min={0} step={0.01}
                      value={t.min_amount}
                      onChange={(e) => updateTierRow(idx, 'min_amount', Number(e.target.value))} />
                  </td>
                  <td className="py-3 px-3">
                    <input type="number" className="border rounded px-3 py-2 w-full" min={0} step={0.01}
                      value={t.rate}
                      onChange={(e) => updateTierRow(idx, 'rate', Number(e.target.value))} />
                  </td>
                  <td className="py-3 px-3 flex gap-2">
                    <button type="button" className="text-red-600 hover:underline" onClick={() => removeTierRow(idx)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-4">
            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-3 rounded">Save Tiers</button>
          </div>
        </form>
      )}
    </div>
  );
};

export default TiersManagement;
